import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Initialize darkMode based on saved preference or system preference
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Effect to handle system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    // Only listen to system changes if no saved preference exists
    const handleChange = (e) => {
      const saved = localStorage.getItem('darkMode');
      if (saved === null) {
        setDarkMode(e.matches);
      }
    };

    // Add listener for system theme changes
    mediaQuery.addEventListener('change', handleChange);

    // Initial check
    if (localStorage.getItem('darkMode') === null) {
      setDarkMode(mediaQuery.matches);
    }

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Effect to apply theme changes
  useEffect(() => {
    // Remove both classes first then add the appropriate one
    document.documentElement.classList.remove('dark', 'light');
    document.documentElement.classList.add(darkMode ? 'dark' : 'light');
    
    // Update toast CSS variables based on theme
    if (darkMode) {
      document.documentElement.style.setProperty('--toastBackground', '#333');
      document.documentElement.style.setProperty('--toastColor', '#fff');
      document.documentElement.style.setProperty('--toastBorder', '#444');
      
      document.documentElement.style.setProperty('--toastSuccessBackground', '#0c4a2d');
      document.documentElement.style.setProperty('--toastSuccessColor', '#fff');
      document.documentElement.style.setProperty('--toastSuccessBorder', '#065f46');
      
      document.documentElement.style.setProperty('--toastErrorBackground', '#4c1d24');
      document.documentElement.style.setProperty('--toastErrorColor', '#fff');
      document.documentElement.style.setProperty('--toastErrorBorder', '#b91c1c');
      
      document.documentElement.style.setProperty('--toastLoadingBackground', '#1e3a8a');
      document.documentElement.style.setProperty('--toastLoadingColor', '#fff');
      document.documentElement.style.setProperty('--toastLoadingBorder', '#1e40af');
    } else {
      document.documentElement.style.setProperty('--toastBackground', '#fff');
      document.documentElement.style.setProperty('--toastColor', '#000');
      document.documentElement.style.setProperty('--toastBorder', '#e2e8f0');
      
      document.documentElement.style.setProperty('--toastSuccessBackground', '#ecfdf5');
      document.documentElement.style.setProperty('--toastSuccessColor', '#065f46');
      document.documentElement.style.setProperty('--toastSuccessBorder', '#d1fae5');
      
      document.documentElement.style.setProperty('--toastErrorBackground', '#fef2f2');
      document.documentElement.style.setProperty('--toastErrorColor', '#b91c1c');
      document.documentElement.style.setProperty('--toastErrorBorder', '#fee2e2');
      
      document.documentElement.style.setProperty('--toastLoadingBackground', '#eff6ff');
      document.documentElement.style.setProperty('--toastLoadingColor', '#1e40af');
      document.documentElement.style.setProperty('--toastLoadingBorder', '#dbeafe');
    }
    
    // Only save to localStorage if it's an explicit user preference
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) {
      localStorage.setItem('darkMode', JSON.stringify(darkMode));
    }
  }, [darkMode]);

  // Function to explicitly set a user preference
  const setUserPreference = (isDark) => {
    setDarkMode(isDark);
    localStorage.setItem('darkMode', JSON.stringify(isDark));
  };

  // Function to reset to system preference
  const resetToSystem = () => {
    localStorage.removeItem('darkMode');
    setDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
  };

  return (
    <ThemeContext.Provider value={{ 
      darkMode, 
      setDarkMode: setUserPreference,
      resetToSystem 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 